import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useCreateVisit } from "@/lib/mutations";
import { usePatientPhotoBlob, usePublicAppSettings, useEffectiveSlotConfig } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { User, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { dateIST } from "@emr/shared/india";
import { RebookDialog } from "./RebookDialog";
import type { AppointmentRow } from "@/lib/queries";

/** Minimal appointment info needed to run a verified check-in. */
export interface CheckInTarget {
  appointmentId: string;
  patientId: string;
  doctorId?: string | null;
  patientName: string | null;
  clinicUhid: string | null;
  /** Passport photo — shown so reception can visually confirm the right patient. */
  photoR2Key?: string | null;
  /** The appointment's current slot key + arrival time — needed by the
   * late-check-in flow (no_available_slot → offer Reschedule via RebookDialog)
   * and to resolve slot labels for the late toast. */
  slot?: string | null;
  arrivalTime?: string | null;
  /** The full row when available (queue pages) — RebookDialog needs it. Falls
   * back to a synthesized row from the target fields (dashboard timeline). */
  row?: AppointmentRow | null;
}

/** POST /visits success payload beyond the visit row (Phase 2 late flow). */
interface CheckInResponse {
  visit: { id: string };
  late?: boolean;
  policy?: "first" | "in_order" | "last";
  pushedToSlot?: string;
  keptSlot?: string;
  overridden?: boolean;
  daySerial?: number;
}

/**
 * Check-in with mobile-number verification. Reception asks the patient for
 * their mobile number, enters it here, and the check-in only completes if the
 * number matches the one registered for the patient (server-verified against
 * phone/alt_phone on POST /visits — the stored number is never shown, so the
 * dialog can't be satisfied by copying what's on screen).
 *
 * The patient's photo (when one exists) is shown beside the name/UHID as a
 * second visual identity cue. Viewing follows patients.view; the blob fetch
 * fails silently (placeholder) if the user lacks it.
 *
 * Late check-in (Phase 3): on success with late=true a toast reports the
 * push ("Checked in late — moved to <label>" + "(admin override)" when the
 * admin capacity-override path fired). On 422 no_available_slot (non-admin,
 * no later slot with capacity) the API's human message is shown inline with
 * a Reschedule action that opens RebookDialog for this appointment.
 */
export function CheckInDialog({ open, onOpenChange, target }: { open: boolean; onOpenChange: (v: boolean) => void; target: CheckInTarget | null }) {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  // no_available_slot state — shows the API message + Reschedule action.
  const [needsReschedule, setNeedsReschedule] = useState(false);
  const [rebookOpen, setRebookOpen] = useState(false);
  const checkIn = useCreateVisit();
  const { data: settings } = usePublicAppSettings();
  // Admins may paste the number (e.g. from the patient record); everyone else
  // must type it as the patient states it — this is a verification step, so
  // pasting defeats the check (see SCHEMA.md §9 mobile verification).
  const { isAdmin } = useAuth();

  // The arrival date's config — resolves slot labels for the late toast.
  const arrivalDate = target?.arrivalTime ? dateIST(target.arrivalTime) : undefined;
  const { slotList } = useEffectiveSlotConfig(arrivalDate, settings);

  const slotLabel = (key?: string | null) =>
    key ? (slotList.find((s) => s.key === key)?.label ?? key) : null;

  // Photo blob + object URL (identity cue). URL lifecycle owned here:
  // created when the blob changes, revoked on the next change/unmount.
  const photoQuery = usePatientPhotoBlob(target?.patientId, target?.photoR2Key);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photoQuery.data) { setPhotoUrl(null); return; }
    const url = URL.createObjectURL(photoQuery.data);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoQuery.data]);

  // Fresh state each time the dialog opens (or switches to another appointment).
  useEffect(() => {
    if (open) {
      setPhone("");
      setError(null);
      setNeedsReschedule(false);
    }
  }, [open, target?.appointmentId]);

  const submit = async () => {
    if (!target?.patientId || !phone.trim() || checkIn.isPending) return;
    setError(null);
    setNeedsReschedule(false);
    try {
      const res = await checkIn.mutateAsync({
        appointmentId: target.appointmentId,
        patientId: target.patientId,
        doctorId: target.doctorId ?? undefined,
        phoneConfirm: phone.trim(),
      }) as CheckInResponse;
      // Late check-in toast (Phase 3): the server already moved the patient
      // (pushedToSlot) or kept their slot under admin time-override
      // (keptSlot). Report where they landed; "(admin override)" marks the
      // admin capacity-override path.
      if (res?.late) {
        const dest = slotLabel(res.pushedToSlot ?? res.keptSlot ?? target.slot);
        toast.info(
          `Checked in late — moved to ${dest ?? "a later slot"}${res.overridden ? " (admin override)" : ""}`,
          { duration: 8000 },
        );
      }
      onOpenChange(false);
    } catch (e) {
      const err = e as Error & { body?: { error?: string; message?: string } };
      const code = err?.body?.error;
      if (code === "phone_mismatch") {
        setError("This mobile number does not match the one registered for the patient. Re-check with the patient and try again.");
      } else if (code === "no_phone_on_file") {
        setError("No mobile number is on file for this patient. Add it from the patient page, then check in.");
      } else if (code === "no_available_slot") {
        // Non-admin late check-in with no later slot having capacity: the
        // patient must be rebooked. Show the API's human message + offer
        // Reschedule (opens RebookDialog for this appointment).
        setNeedsReschedule(true);
        setError(err?.body?.message || "The patient is past this slot's check-in window and no later slot has capacity today. Rebook them to another day, or ask an administrator to override the check-in.");
      } else {
        setError(err?.body?.message || err?.body?.error || "Check-in failed. Please try again.");
      }
    }
  };

  // Row for RebookDialog: prefer the caller's full row; synthesize from the
  // target otherwise (dashboard timeline rows carry the needed fields).
  const rebookRow: AppointmentRow | null = target
    ? (target.row ?? {
        id: target.appointmentId,
        patientId: target.patientId,
        doctorId: target.doctorId ?? null,
        slot: target.slot ?? null,
        arrivalTime: target.arrivalTime ?? null,
        checkInAt: null,
        queueOrder: null,
        daySerial: null,
        status: "Booked",
        reason: null,
        whatsappReminderEnabled: null,
        originVisitId: null,
        bookedByUserId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        patientName: target.patientName,
        clinicUhid: target.clinicUhid,
        dob: null,
        gender: null,
        phone: null,
        photoR2Key: target.photoR2Key ?? null,
        doctorName: null,
        visitId: null,
        flags: [],
        isNewPatient: null,
      })
    : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Check-in — verify mobile number</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {/* Passport photo (when present) — visual identity cue. */}
              {target?.photoR2Key && (
                <div className="h-[74px] w-[58px] shrink-0 overflow-hidden rounded-md border border-cream-200 bg-cream-50 dark:border-dark-700 dark:bg-dark-800">
                  {photoUrl ? (
                    <img src={photoUrl} alt="Patient photo" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted"><User size={24} /></div>
                  )}
                </div>
              )}
              <div className="min-w-0 text-sm text-muted">
                <div className="truncate font-medium text-ink dark:text-cream-50">{target?.patientName ?? "Unknown"}</div>
                {target?.clinicUhid && <div className="text-xs">{target.clinicUhid}</div>}
              </div>
            </div>
            <div>
              <Label htmlFor="checkin-phone">Patient's mobile number</Label>
              <Input
                id="checkin-phone"
                inputMode="numeric"
                autoComplete="off"
                placeholder="Enter number as stated by the patient"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setError(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                onPaste={(e) => {
                  if (!isAdmin) {
                    e.preventDefault();
                    setError("Pasting is disabled — please type the number as the patient states it.");
                  }
                }}
                onDrop={(e) => { if (!isAdmin) e.preventDefault(); }}
                onCopy={(e) => { if (!isAdmin) e.preventDefault(); }}
                onCut={(e) => { if (!isAdmin) e.preventDefault(); }}
                onContextMenu={(e) => { if (!isAdmin) e.preventDefault(); }}
                autoFocus
              />
              <p className="mt-1 text-xs text-muted">
                Ask the patient for their mobile number and enter it here. Check-in completes only if it matches the number registered for this patient.
              </p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {needsReschedule && (
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => setRebookOpen(true)}
              >
                <CalendarClock size={14} className="mr-1" /> Reschedule this appointment
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!phone.trim() || checkIn.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              {checkIn.isPending ? "Verifying…" : "Verify & check in"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {rebookRow && (
        <RebookDialog open={rebookOpen} onOpenChange={(v) => { setRebookOpen(v); if (!v) onOpenChange(false); }} appointment={rebookRow} />
      )}
    </>
  );
}
